import store from '../redux/store';
import { IImportStructureFields } from '../redux/import/sliceImportViewData';
import { getDirectoryName, getParentDirectory } from './StringUtils';
import { getAssignedDocsCount } from './ImportUtils';
import { IProjectDocumentInfoItem } from '../redux/project/documents/sliceProjectDocuments';
import { IProjectCategorizationStructure } from '../redux/general/sliceCategorization';
import { StructureViewMode } from '../types';

/** Fixed name from HCL Notes backend. */
export const unassignedCategoryName = '(nicht zugeordnet)';

export interface IStructureItemInfo {
    id: string;
    path: string;
    name: string;
    parent: IImportStructureFields | undefined;
    children: IImportStructureFields[];
    docCount?: number;
    isSkeleton?: boolean;
}

export const isSubpathOf = (path1: string, path2: string): boolean => {
    return getSubpaths(path1).some(subpath => subpath === path2);
};

export const getSubpaths = (path: string): string[] => {
    return path.split('\\').map((_, i, parts) => {
        return parts.slice(0, i + 1).join('\\');
    });
};

export const getStructureItemChildren = (
    all: IImportStructureFields[],
    item: IImportStructureFields,
): IImportStructureFields[] => {
    return all.filter(x => item.Subject === getParentDirectory(x.Subject));
};

export const getStructureItemParent = (
    all: IImportStructureFields[],
    item: IImportStructureFields,
): IImportStructureFields | undefined => {
    return all.find(x => x.Subject === getParentDirectory(item.Subject));
};

export const getStructureItemInfo = (
    all: IImportStructureFields[],
    item: IImportStructureFields,
    withDocCount = false,
): IStructureItemInfo => {
    return {
        id: item.IdDocIndex,
        path: item.Subject,
        isSkeleton: item.IsSkeleton,
        name: getDirectoryName(item.Subject),
        parent: getStructureItemParent(all, item),
        children: getStructureItemChildren(all, item),
        docCount: withDocCount ? getAssignedDocsCount(item) : undefined,
    };
};

export const getUnassignedCategory = (docCount?: number): IStructureItemInfo => {
    return {
        id: '/',
        path: '/',
        isSkeleton: true,
        name: unassignedCategoryName,
        parent: undefined,
        children: [],
        docCount: docCount,
    };
};

export const getAscendants = (
    item: string | IStructureItemInfo,
    all?: IStructureItemInfo[],
): IStructureItemInfo[] => {
    if (!all) {
        const state = store.getState();
        const view = state.importData.currentView;
        const structure = state.importViewData.viewStructures.data[view];
        all = (structure ?? []).map(x => getStructureItemInfo(structure, x));
    }

    if (typeof item === 'string') {
        item = all.find(x => x.id === item);
    }

    const ascendants: IStructureItemInfo[] = [];

    item.path.split('\\').forEach((_, i, parts) => {
        const ascendantPath = parts.slice(0, i + 1).join('\\');
        const ascendant = all.find(x => x.path === ascendantPath);

        if (ascendant) {
            ascendants.push(ascendant);
        }
    });

    return ascendants;
};

export const mapCheckedCategoriesToRequest = (
    selectedDocuments: IProjectDocumentInfoItem[],
    structure: IProjectCategorizationStructure[],
    structureName: StructureViewMode,
    fullChecked: string[],
    halfChecked: string[],
) => {
    const idPayloadName = `${structureName}ID`;

    return selectedDocuments.reduce((acc, item) => {
        const itemStructureIds = [
            ...item[idPayloadName].filter(str => halfChecked.includes(str)),
            ...fullChecked,
        ];
        const itemStructurePaths = structure
            .filter(str => itemStructureIds.includes(str.IdDocIndex))
            .map(str => str.Subject);
        return {
            ...acc,
            [item.universalId]: {
                [structureName]: itemStructurePaths,
                [idPayloadName]: itemStructureIds,
            },
        };
    }, {});
};
